import { GzPolicyUiSettingsGridItemFormWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/form';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { customElement, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_SECURITY_SERVERS_SVA_IPS_ITEM } from '../../../../../../../webcomponents.constants';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../ui/gzInput';
import { GzHardCodedFormField } from '../../../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { customHostnameValidators } from './utils/validators';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import {
  PolicyDependenciesContainer,
  UnifiedEndpointsApiClients,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { GzSingleSelectWebComponent } from '../../../../../../../ui/gzSingleSelectDropdown';
import { LabelWidthValues } from '../../../../../../../types/common/types';
import { UnifiedEndpointsSvaMachine } from '@nirvana/shared/dtos/types/policies/common';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_SECURITY_SERVERS_SVA_IPS_ITEM)
export class GzAntimalwareSecurityServersSvaIpsItemFormWebComponent extends GzPolicyUiSettingsGridItemFormWebComponent {
  @query('#itemPriority')
  private itemPriority: GzFormFieldWebComponent<GzInputNumberWebComponent>;

  @query('#itemId')
  private itemId: GzFormFieldWebComponent<GzSingleSelectWebComponent>;

  @query('#itemIp')
  private itemIp: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#itemCustomHostname')
  private itemCustomHostname: GzFormFieldWebComponent<GzInputWebComponent, string>;

  private itemEndpointName: GzHardCodedFormField<string> = new GzHardCodedFormField('');
  private itemProtectedEntityId: GzHardCodedFormField<string> = new GzHardCodedFormField('');

  private clients: UnifiedEndpointsApiClients = PolicyDependenciesContainer.getInstance().resolveDependency(
    'UnifiedEndpoints'
  ) as UnifiedEndpointsApiClients;
  private currentSvaMachines: UnifiedEndpointsSvaMachine[] = [];

  public setError(error: string) {
    this.itemId.setError(error);
  }

  public resetPrioritySettings(max: number) {
    this.itemPriority.Input.max = max;
    this.itemPriority.value = max;
  }

  renderFormFields(): TemplateResult {
    return html`
      <div class="policy-grid-item-form--field">
        <gz-form-field id="itemPriority">
          <gz-input-number
            gz-form-input
            .min=${1}
            inputWidth="${InputWidthValues.XSMALL}"
            label="${translateFn('Priority')}"
          ></gz-input-number>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="itemId">
          <gz-single-select
            style="--dropdown-list-width: 320px;"
            gz-form-input
            labelWidth="${LabelWidthValues.XLARGE}"
            label="${translateFn('Security Server')}"
            placeholder="${translateFn('No Security Server selected')}"
            searchPlaceholderString="${translateFn('Search')}"
            noSearchResultsString="${translateFn('No Security Server available.')}"
            @open="${this.onSecurityServersDropdownOpen}"
            @selectionChange="${this.onSecurityServersSelectionChanged}"
          ></gz-single-select>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="itemIp" class="disabled">
          <gz-input gz-form-input label="${translateFn('IP')}"></gz-input>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="itemCustomHostname">
          <gz-input
            gz-form-input
            inputWidth="${InputWidthValues.XLARGE}"
            label="${translateFn('Custom server name/IP')}"
            placeholder="${translateFn('Custom server name/IP')}"
          ></gz-input>
        </gz-form-field>
      </div>
    `;
  }

  private async onSecurityServersDropdownOpen() {
    this.itemId.Input.isLoading = true;

    try {
      const result = await this.clients.getSvaMachines();
      this.currentSvaMachines = result.data;

      this.itemId.Input.filterOptions = this.currentSvaMachines.map(item => {
        return {
          label: item.name + ' (' + item.ip + ')',
          value: item._id,
        };
      });
    } catch (error) {
      this.setError(translateFn('Unable to load the Security Servers list.'));
      this.currentSvaMachines = [];
    } finally {
      this.itemId.Input.isLoading = false;
    }
  }

  private onSecurityServersSelectionChanged(event: CustomEvent) {
    // get data
    const data = this.currentSvaMachines.find(item => item._id === event.detail.selectedValue);

    this.itemEndpointName.value = data?.name ?? '';
    this.itemProtectedEntityId.value = data?.protectedEntityId ?? '';
    this.itemIp.value = data?.ip ?? '';
    this.itemCustomHostname.value = data?.customHostname ?? '';
  }

  focusFirstInput(): void {
    this.itemPriority.Input.inputFocus();
  }

  createFormGroup(): void {
    this.itemId.Validators = [RequiredValidator(translateFn('Required field'))];
    this.itemCustomHostname.Validators = customHostnameValidators();

    this.formGroup = new FormGroup({
      priority: this.itemPriority,
      endpointId: this.itemId,
      protectedEntityId: this.itemProtectedEntityId,
      name: this.itemEndpointName,
      ip: this.itemIp,
      customHostname: this.itemCustomHostname,
    });
  }

  resetForm(): void {
    this.itemId.value = '';
    this.itemId.resetField();
    this.itemIp.value = '';
    this.itemIp.resetField();
    this.itemCustomHostname.value = '';
    this.itemCustomHostname.resetField();
    this.itemEndpointName.value = '';
    this.itemProtectedEntityId.value = '';

    this.itemId.validate();
    this.itemCustomHostname.validate();
  }

  reset() {
    this.resetForm();
  }

  resetOnAdd() {
    this.resetForm();
  }
}
